import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { Tag } from '@/components/Tag';
import Input from '@/components/Input';
import Select from '@/components/Select';
import useJobPostTags from '../hooks/useJobPostTags';
import useCreateJobPostTag from '../hooks/useCreateJobPostTag';
import useUpdateJobPostTag from '../hooks/useUpdateJobPostTag';
import useDeleteJobPostTag from '../hooks/useDeleteJobPostTag';
import { showSuccessToast, showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import TagManagementList from './TagManagementList';
import TagSettingsForm from './TagSettingsForm';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import { TAG_COLORS, COLOR_OPTIONS } from '@/constants/tagColors';
import type { AgentApiTagsRecord } from '@/types/serializers';

export default function JobPostTagsTemplate() {
  const { data: tagsData, isLoading, error, refetch } = useJobPostTags();
  const createTagMutation = useCreateJobPostTag();
  const updateTagMutation = useUpdateJobPostTag();
  const deleteTagMutation = useDeleteJobPostTag();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<AgentApiTagsRecord | null>(null);
  const [editingTag, setEditingTag] = useState<AgentApiTagsRecord | null>(null);

  // 編集フォーム用の状態
  const [editTagName, setEditTagName] = useState('');
  const [editSelectedColor, setEditSelectedColor] = useState<(typeof TAG_COLORS)[0] | null>(null);

  // 新規作成フォーム用の状態
  const [createTagName, setCreateTagName] = useState('');
  const [createSelectedColor, setCreateSelectedColor] = useState<(typeof TAG_COLORS)[0]>(
    TAG_COLORS[0]
  );

  // ページ遷移時にタグが存在する場合、最初のタグを編集モードにする
  useEffect(() => {
    if (tagsData?.records && tagsData.records.length > 0 && !editingTag) {
      const firstTag = tagsData.records[0];
      handleEditTag(firstTag);
    }
  }, [tagsData?.records, editingTag]);

  const handleCreateTag = () => {
    setIsCreateDialogOpen(true);
  };

  const handleEditTag = (tag: AgentApiTagsRecord) => {
    setEditingTag(tag);
    setEditTagName(tag.name);
    const color = TAG_COLORS.find((c) => c.value === tag.color_hex) || TAG_COLORS[0];
    setEditSelectedColor(color);
  };

  const handleDeleteTag = (tag: AgentApiTagsRecord) => {
    setTagToDelete(tag);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!tagToDelete) return;

    try {
      await deleteTagMutation.mutateAsync(tagToDelete.id);
      showSuccessToast('タグを削除しました');
      setIsDeleteDialogOpen(false);
      setTagToDelete(null);

      // 削除後にタグ一覧を再取得
      const { data: updatedTagsData } = await refetch();

      // 削除したタグが編集中のタグだった場合、編集モードをリセット
      if (editingTag?.id === tagToDelete.id) {
        setEditingTag(null);
        setEditTagName('');
        setEditSelectedColor(null);

        // 他のタグが残っている場合は最初のタグを編集モードにする
        if (updatedTagsData?.records && updatedTagsData.records.length > 0) {
          const firstTag = updatedTagsData.records[0];
          handleEditTag(firstTag);
        }
      }
    } catch (error) {
      console.error('タグ削除エラー:', error);
      showErrorToast('タグの削除に失敗しました');
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setTagToDelete(null);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setCreateTagName('');
    setCreateSelectedColor(TAG_COLORS[0]);
  };

  const handleSubmitCreateTag = async () => {
    try {
      const createdTag = await createTagMutation.mutateAsync({
        name: createTagName,
        color_hex: createSelectedColor?.value || TAG_COLORS[0].value,
      });
      showSuccessToast('タグを追加しました');
      handleCloseCreateDialog();

      // 作成したタグを編集モードにする
      if (createdTag) {
        handleEditTag(createdTag);
      }
    } catch (error) {
      console.error('タグ作成エラー:', error);
      showErrorToast('タグの作成に失敗しました');
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag) return;

    try {
      await updateTagMutation.mutateAsync({
        id: editingTag.id,
        name: editTagName,
        color_hex: editSelectedColor?.value || TAG_COLORS[0].value,
      });
      showSuccessToast('タグを更新しました');
    } catch (error) {
      console.error('タグ更新エラー:', error);
      showErrorToast('タグの更新に失敗しました');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-lg py-sm">
        <div className="flex h-64 items-center justify-center">
          <div className="text-red-600">エラーが発生しました: {error.message}</div>
        </div>
      </div>
    );
  }

  // 共通の戻るボタンコンポーネント
  const BackButton = () => (
    <div className="mb-xs">
      <Link to="/agent/provided_job_posts">
        <Button color="tertiary" size="md" className="gap-1">
          <svg
            width="6"
            height="11"
            viewBox="0 0 6 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="size-5"
          >
            <path
              d="M5.01396 10.0277L0 5.01375L5.01396 0L6 0.986042L1.97229 5.01375L6 9.04167L5.01396 10.0277Z"
              fill="currentColor"
            />
          </svg>
          求人管理画面に戻る
        </Button>
      </Link>
    </div>
  );

  // タグがない場合の表示
  const renderEmptyState = () => (
    <div className="flex justify-center">
      <div className="w-full max-w-[1142px] rounded-lg border border-border bg-white p-24 shadow-xs">
        <div className="flex flex-col items-center justify-center gap-6">
          {/* タグアイコン */}
          <div className="relative">
            <svg
              width="151"
              height="168"
              viewBox="0 0 152 168"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="size-32"
            >
              <path
                d="M65.0893 162.781L25.3137 123.005C18.4162 116.108 18.4162 104.934 25.3137 98.0824L83.8964 39.4997C85.5059 37.8903 87.6671 37.0166 89.9202 37.0166L133.65 37.2005C143.169 37.2005 150.894 44.9717 150.94 54.4902L151.124 98.2203C151.124 100.473 150.25 102.635 148.641 104.244L90.0582 162.827C83.1607 169.724 71.9868 169.724 65.1353 162.827L65.0893 162.781Z"
                fill="#1E4195"
              />
              <path
                d="M130.983 61.2037L130.799 17.4736C130.799 7.95511 123.028 0.229914 113.51 0.18393L69.7796 0C67.5265 0 65.3653 0.87368 63.7558 2.48309L5.17312 61.0658C-1.72437 67.9633 -1.72437 79.1372 5.17312 85.9887L44.9487 125.764C51.8462 132.662 63.0201 132.662 69.8716 125.764L128.454 67.1816C130.064 65.5721 130.937 63.4109 130.937 61.1578L130.983 61.2037ZM101.416 29.6132C97.8294 26.0265 97.8294 20.1866 101.416 16.6C105.003 13.0133 110.843 13.0133 114.429 16.6C118.016 20.1866 118.016 26.0265 114.429 29.6132C110.843 33.1999 105.003 33.1999 101.416 29.6132Z"
                fill="#275DF6"
              />
            </svg>
          </div>

          {/* タイトル */}
          <h2 className="text-lg font-bold text-main">求人をタグで管理</h2>

          {/* 説明文 */}
          <p className="text-center text-base">
            職種・勤務地・条件などで求人を分類し、スムーズに対応・検索できます。
          </p>

          {/* ボタン */}
          <Button onClick={handleCreateTag} className="h-10 w-64">
            タグを新規追加
          </Button>
        </div>
      </div>
    </div>
  );

  // タグ一覧の表示
  const renderTagList = () => (
    <div className="flex items-start gap-xs">
      <TagManagementList
        tags={tagsData?.records || []}
        editingTag={editingTag}
        onEditTag={handleEditTag}
        onCreateTag={handleCreateTag}
        onDeleteTag={handleDeleteTag}
      />
      <TagSettingsForm
        editingTag={editingTag}
        tagName={editTagName}
        selectedColor={editSelectedColor}
        onTagNameChange={setEditTagName}
        onColorChange={setEditSelectedColor}
        onDeleteTag={handleDeleteTag}
        onUpdateTag={handleUpdateTag}
        isUpdating={updateTagMutation.isPending}
      />
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-lg py-sm">
      <BackButton />

      {tagsData?.records && tagsData.records.length > 0 ? renderTagList() : renderEmptyState()}

      {/* タグ新規追加ダイアログ */}
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleCloseCreateDialog();
        }}
      >
        <DialogContent className="w-full max-w-[860px] rounded shadow-xl!">
          <div className="flex items-center justify-between px-6 py-4">
            <DialogTitle className="text-lg font-bold!">タグの新規追加</DialogTitle>
          </div>

          <div className="p-6">
            <div className="space-y-6">
              <div>
                <label className="mb-xs flex items-center gap-xs">
                  <span className="text-heading-xxs text-main">タグタイトル</span>
                  <Tag color="danger" size="sm">
                    必須
                  </Tag>
                </label>
                <Input
                  value={createTagName}
                  onChange={(e) => setCreateTagName(e.target.value)}
                  placeholder="タグタイトル（15文字以内）"
                  maxLength={15}
                  className="w-full"
                />
              </div>

              <div className="w-[218px]">
                <label className="mb-xs block text-heading-xxs text-main">カラー選択</label>
                <Select
                  items={COLOR_OPTIONS}
                  value={createSelectedColor?.label || ''}
                  onValueChange={(value) => {
                    const color = TAG_COLORS.find((c) => c.label === value);
                    if (color) setCreateSelectedColor(color);
                  }}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 px-6 py-4">
            <Button color="secondary" onClick={handleCloseCreateDialog}>
              キャンセル
            </Button>
            <Button
              color="primary"
              onClick={handleSubmitCreateTag}
              disabled={!createTagName.trim() || createTagMutation.isPending}
            >
              {createTagMutation.isPending ? '送信中...' : '追加する'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        tagName={tagToDelete?.name}
        isDeleting={deleteTagMutation.isPending}
      />
    </div>
  );
}
